import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgbDropdown, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UnitOfWorkService } from '../../services/unit-of-work/unit-of-work.service';
import { ApplicationInsightsService } from '../../services/application-insights/application-insights.service';
import { Observable } from 'rxjs';
import { DialogHelper } from '../../services/dialog-helper';
import { IsBusyService } from '../../services/is-busy/is-busy.service';
import * as model from '../../../model/model';
import * as modelDto from '../../../model/modelDto';
import { GlobalErrorHandler } from '../../services/global-error-handler';
import { SharedDataService } from '../../services/shared-data/shared-data.service';

interface SecurityActivityLogFilterState {
    changedBy: string;
    changedEmployee: string;
    oldValue: string;
    newValue: string;
    changedFromUtc: string;
    changedToUtc: string;
}

@Component({
    selector: 'app-security-roles',
    templateUrl: './security-roles.component.html',
    standalone: false
})
export class SecurityRolesAdminComponent implements OnInit {
    @ViewChild('activityLogModal') activityLogModal: TemplateRef<any>;

    employees: model.Employee[] = [];
    employeeSearchResults: model.Employee[] = [];
    activityLog: modelDto.EmployeeSecurityRoleChangeDto[] = [];
    activityLogFilters = this.emptyActivityLogFilters();
    draftActivityLogFilters = this.emptyActivityLogFilters();
    activityLogDateFilterError = '';
    readonly activityLogRoleOptions = ['User', 'Captain', 'Admin'];

    selectedEmployeeId: string = '';

    showCaptains: boolean = false;
    showAdmins: boolean = false;
    isLoadingActivityLog = false;
    activityLogLoadError = false;

    constructor(
        private uow: UnitOfWorkService,
        private appInsights: ApplicationInsightsService,
        private errorHandler: GlobalErrorHandler,
        public isBusy: IsBusyService,
        public dialogHelper: DialogHelper,
        private sharedData: SharedDataService,
        private router: Router,
        private modalService: NgbModal
    ) {
        appInsights.logPageView();
    }

    ngOnInit() {
        this.handleSecurity();
    }

    roleChanged(employee: model.Employee, role: 'user' | 'captain' | 'admin') {
        switch (role) {
            case 'admin':
                if (employee.isAdmin) {
                    employee.isNationalQcAdmin = true;
                    employee.canSignIn = true;
                }
                break;
            case 'captain':
                employee.isAdmin = false;
                employee.canSignIn = true;
                break;
            case 'user':
                employee.isAdmin = false;
                employee.isNationalQcAdmin = false;
                break;
        }

        this.save();
    }

    openActivityLog() {
        if (this.isLoadingActivityLog) {
            return;
        }

        this.resetActivityLogFilters();
        this.modalService.open(this.activityLogModal, {
            size: 'xl',
            scrollable: true,
            ariaLabelledBy: 'security-role-activity-log-title'
        });

        this.loadActivityLog();
    }

    onActivityLogFilterOpen(isOpen: boolean) {
        if (isOpen) {
            this.draftActivityLogFilters = { ...this.activityLogFilters };
            this.activityLogDateFilterError = '';
        }
    }

    applyActivityLogFilters(dropdown?: NgbDropdown) {
        if (!this.activityLogDateRangeIsValid()) {
            return;
        }

        this.activityLogFilters = { ...this.draftActivityLogFilters };
        dropdown?.close();
        this.loadActivityLog();
    }

    clearActivityLogFilter(field: keyof SecurityActivityLogFilterState) {
        this.draftActivityLogFilters = {
            ...this.draftActivityLogFilters,
            [field]: null
        };
        this.activityLogDateFilterError = '';
    }

    clearActivityLogFilters(dropdown?: NgbDropdown) {
        this.resetActivityLogFilters();
        dropdown?.close();
        this.loadActivityLog();
    }

    get hasActivityLogFilters() {
        return Object.values(this.activityLogFilters).some(value => !!value);
    }

    private loadActivityLog() {
        this.activityLog = [];
        this.activityLogLoadError = false;
        this.isLoadingActivityLog = true;
        const filters = this.buildActivityLogFilters();
        const request = filters.length
            ? this.uow.getEmployeeSecurityRoleChanges(JSON.stringify(filters))
            : this.uow.getEmployeeSecurityRoleChanges();

        request
            .then(result => {
                this.activityLog = result || [];
            })
            .catch(reason => {
                this.activityLogLoadError = true;
                this.errorHandler.handleError(reason);
            })
            .finally(() => {
                this.isLoadingActivityLog = false;
            });
    }

    private buildActivityLogFilters() {
        const filters: modelDto.GridFilter[] = [];
        const values = this.activityLogFilters;
        const add = (key: string, value: string) => {
            if (value?.trim()) {
                filters.push({ key, value: value.trim() } as modelDto.GridFilter);
            }
        };

        add('changedBy', values.changedBy);
        add('changedEmployee', values.changedEmployee);
        add('oldValue', values.oldValue);
        add('newValue', values.newValue);
        if (values.changedFromUtc) {
            add('changedFromUtc', this.toUtcFilterValue(values.changedFromUtc));
        }
        if (values.changedToUtc) {
            add('changedToUtc', this.toUtcFilterValue(values.changedToUtc));
        }
        return filters;
    }

    private activityLogDateRangeIsValid() {
        const from = this.draftActivityLogFilters.changedFromUtc;
        const to = this.draftActivityLogFilters.changedToUtc;
        if (from && to && from > to) {
            this.activityLogDateFilterError = 'Changed From must be before or equal to Changed To.';
            return false;
        }

        this.activityLogDateFilterError = '';
        return true;
    }

    private toUtcFilterValue(value: string) {
        return new Date(`${value}Z`).toISOString();
    }

    private resetActivityLogFilters() {
        this.activityLogFilters = this.emptyActivityLogFilters();
        this.draftActivityLogFilters = this.emptyActivityLogFilters();
        this.activityLogDateFilterError = '';
    }

    private emptyActivityLogFilters(): SecurityActivityLogFilterState {
        return {
            changedBy: null,
            changedEmployee: null,
            oldValue: null,
            newValue: null,
            changedFromUtc: null,
            changedToUtc: null
        };
    }

    getActivityLogValue(value: string | null) {
        return value && value.trim() ? value : 'None';
    }

    formatActivityLogDate(value: Date | string) {
        if (!value) {
            return '';
        }

        const rawValue = value instanceof Date ? value.toISOString() : value;
        const utcValue = /(?:Z|[+-]\d{2}:\d{2})$/.test(rawValue) ? rawValue : `${rawValue}Z`;
        const date = new Date(utcValue);
        if (Number.isNaN(date.getTime())) {
            return '';
        }

        const pad = (part: number) => part.toString().padStart(2, '0');
        return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ` +
            `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())} UTC`;
    }

    searchAllEmployees() {
        return this.uow.getEmployees(null, null, this.showAdmins, this.showCaptains)
            .then(result => {
                this.employees = result.filter(m => m.isActive);
                this.employeeSearchResults = this.employees;
            });
    }

    searchSpecificEmployee(searchTerm: string) {
        return this.uow.getEmployees(searchTerm)
            .then(result => {
                this.employees = result.filter(m => m.isActive);
            });
    }

    employeeChanged() {
        if (!this.selectedEmployeeId) {
            this.searchAllEmployees();
            return;
        }

        this.employeeSearchResults = this.employees.filter(x => x.id === this.selectedEmployeeId);
    }

    save() {
        this.isBusy.set('admin:saveSecurityRole', true, "Saving Changes");
        this.uow.employees.saveChanges()
            .catch(reason => {
                this.errorHandler.handleError(reason);
            })
            .finally(() => {
                this.isBusy.set('admin:saveSecurityRole', false);
                this.selectedEmployeeId = '';
                this.handleSecurity();
            });
    }

    handleSecurity() {
        if (!this.sharedData.me.value.isAdmin) {
            this.router.navigate(['']);
        } else {
            this.searchAllEmployees();
        }
    }
}
